import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Star, Clock, Sparkles, Heart, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { EditableText, EditableImage } from '../components/InlineEdit';
import axios from 'axios';
import GoogleReviews from '../components/GoogleReviews';
import InstagramPage from '../components/InstagramPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const HomePage = () => {
  const { t, language } = useLanguage();
  const { user, isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const { settings, getImageUrl } = useSiteSettings();
  const [services, setServices] = useState([]);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services`);
        setServices(res.data);
      } catch (err) {
        console.error('Error fetching services:', err);
      }
    };
    fetchServices();
  }, []);

  const handleBookClick = () => {
    if (isAuthenticated) {
      navigate('/booking');
    } else {
      login();
    }
  };

  // Get theme colors with fallbacks
  const primaryColor = settings?.primary_color || '#BFA05F';
  const secondaryColor = settings?.secondary_color || '#1A1815';
  const backgroundColor = settings?.background_color || '#F9F8F6';
  const textColor = settings?.text_color || '#1A1815';
  
  // Button colors
  const buttonBgColor = settings?.button_bg_color || '#1A1815';
  const buttonTextColor = settings?.button_text_color || '#FFFFFF';
  const buttonHoverBgColor = settings?.button_hover_bg_color || '#BFA05F';
  const buttonHoverTextColor = settings?.button_hover_text_color || '#FFFFFF';

  // Get first 3 services for display
  const featuredServices = services.slice(0, 3);

  return (
    <div className="min-h-screen" style={{ backgroundColor }} data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" style={{ overflow: 'hidden' }}>
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <EditableImage
            settingKey="hero_image"
            defaultSrc="https://res.cloudinary.com/dwpnw1fgc/image/upload/v1770140140/496752021_9642235385888846_6183341768593877756_n_z3gkkp.jpg"
            alt="Lou's Way"
            className="w-full h-full"
            imgClassName="w-full h-full object-cover"
          />
        </div>
        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 z-[1] pointer-events-none" 
          style={{ background: `linear-gradient(to right, ${backgroundColor} 0%, ${backgroundColor}F0 30%, ${backgroundColor}80 60%, transparent 100%)` }} 
        />

        {/* Content */}
        <div className="relative z-[5] max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-20">
          <div className="max-w-2xl animate-fade-in-up">
            <span className="text-sm uppercase tracking-widest font-medium" style={{ color: primaryColor }}>
              <EditableText settingKey="brand_name" defaultValue="Lou's Way" />
            </span>
            {/* O EditableText pode renderizar um elemento block/inline-block. Sem esta regra,
                a frase principal ocupa uma caixa própria e empurra «em você!» para baixo.
                Ambos têm de fluir na MESMA linha de texto, com quebra apenas quando necessário. */}
            <style>{`
              /* EditableText pode criar uma caixa block/inline-block;
                 só a caixa do conteúdo precisa de ficar inline. Não mexer nos botões do editor. */
              .lousway-hero-headline .lousway-hero-normal,
              .lousway-hero-headline .lousway-hero-normal > :first-child,
              .lousway-hero-headline .lousway-hero-normal > :first-child > :first-child,
              .lousway-hero-headline .lousway-hero-gold,
              .lousway-hero-headline .lousway-hero-gold > :first-child,
              .lousway-hero-headline .lousway-hero-gold > :first-child > :first-child {
                display: inline !important;
                float: none !important;
                clear: none !important;
                width: auto !important;
              }
              .lousway-hero-headline .lousway-hero-normal,
              .lousway-hero-headline .lousway-hero-normal > :first-child,
              .lousway-hero-headline .lousway-hero-normal > :first-child > :first-child {
                white-space: normal !important;
              }
              .lousway-hero-headline .lousway-hero-gold,
              .lousway-hero-headline .lousway-hero-gold > :first-child,
              .lousway-hero-headline .lousway-hero-gold > :first-child > :first-child {
                white-space: nowrap !important;
              }
            `}</style>
            <h1 className="lousway-hero-headline font-heading text-5xl md:text-7xl font-medium tracking-tight leading-tight mt-4" style={{ color: textColor }}>
              <span className="lousway-hero-normal"><EditableText settingKey={`hero_title_${language}`} defaultValue={t('home.hero_title')} /></span>{' '}
              <span className="lousway-hero-gold italic" style={{ color: primaryColor }}><EditableText settingKey={`hero_title_accent_${language}`} defaultValue={t('home.hero_title_accent')} /></span>
            </h1>
            <p className="text-lg md:text-xl mt-6 leading-relaxed max-w-xl" style={{ color: `${textColor}99` }}>
              <EditableText 
                settingKey={`hero_subtitle_${language}`} 
                defaultValue={t('home.hero_subtitle')} 
                multiline 
              />
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Button
                onClick={handleBookClick}
                className="rounded-full px-8 py-6 text-sm uppercase tracking-wide btn-luxury transition-colors duration-300"
                style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = buttonHoverBgColor;
                  e.currentTarget.style.color = buttonHoverTextColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = buttonBgColor;
                  e.currentTarget.style.color = buttonTextColor;
                }}
                data-testid="hero-book-btn"
              >
                <EditableText settingKey={`btn_book_${language}`} defaultValue={t('home.cta_book')} />
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/services')}
                className="rounded-full px-8 py-6 text-sm uppercase tracking-wide transition-colors duration-300"
                style={{ borderColor: buttonBgColor, color: buttonBgColor, backgroundColor: 'transparent' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = buttonBgColor;
                  e.currentTarget.style.color = buttonTextColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = buttonBgColor;
                }}
                data-testid="hero-discover-btn"
              >
                <EditableText settingKey={`btn_discover_${language}`} defaultValue={t('home.cta_discover')} />
              </Button>
            </div>
          </div>
        </div>      
      </section>

      {/*SECÇÃO DE AVALIAÇÕES GOOGLE */}
      <GoogleReviews />

      <InstagramPage />
      {/* Why Us Section */}

      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16 animate-fade-in-up">
            <span className="text-sm uppercase tracking-widest font-medium" style={{ color: primaryColor }}>
              <EditableText settingKey={`why_us_label_${language}`} defaultValue={t('home.why_us')} />
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-normal tracking-tight mt-4" style={{ color: textColor }}>
              <EditableText settingKey={`why_us_title_${language}`} defaultValue={language === 'pt' ? 'Porquê Escolher-nos' : 'Why Choose Us'} />
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Star, titleKey: 'why_us_1_title', descKey: 'why_us_1_desc', defaultTitle: t('home.why_us_1_title'), defaultDesc: t('home.why_us_1_desc') },
              { icon: Heart, titleKey: 'why_us_2_title', descKey: 'why_us_2_desc', defaultTitle: t('home.why_us_2_title'), defaultDesc: t('home.why_us_2_desc') },
              { icon: Shield, titleKey: 'why_us_3_title', descKey: 'why_us_3_desc', defaultTitle: t('home.why_us_3_title'), defaultDesc: t('home.why_us_3_desc') },
            ].map((item, index) => (
              <div
                key={index}
                className="bg-[#F2F0EB] p-10 rounded-3xl hover:bg-white hover:shadow-xl transition-all duration-500 card-hover animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`why-us-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: secondaryColor }}>
                  <item.icon className="w-6 h-6" style={{ color: primaryColor }} />
                </div>
                <h3 className="font-heading text-2xl font-normal mb-3" style={{ color: textColor }}>
                  <EditableText settingKey={`${item.titleKey}_${language}`} defaultValue={item.defaultTitle} />
                </h3>
                <p className="leading-relaxed" style={{ color: `${textColor}80` }}>
                  <EditableText settingKey={`${item.descKey}_${language}`} defaultValue={item.defaultDesc} multiline />
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Services - From API */}
      <section className="py-24 md:py-32" style={{ backgroundColor }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div className="animate-fade-in-up">
              <span className="text-sm uppercase tracking-widest font-medium" style={{ color: primaryColor }}>
                <EditableText settingKey={`featured_label_${language}`} defaultValue={t('home.featured')} />
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-normal tracking-tight mt-4" style={{ color: textColor }}>
                <EditableText settingKey={`services_title_${language}`} defaultValue={t('services.title')} />
              </h2>
            </div>
            <Link
              to="/services"
              className="mt-6 md:mt-0 hover:opacity-70 transition-colors flex items-center gap-2 text-sm uppercase tracking-widest"
              style={{ color: secondaryColor }}
              data-testid="view-all-services"
            >
              {t('common.view_all')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Services Grid - Dynamic from API */}
          {featuredServices.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-[minmax(200px,auto)]">
              {/* Large Feature Cell - First Service */}
              {featuredServices[0] && (
                <div
                  className="md:col-span-8 md:row-span-2 rounded-3xl overflow-hidden relative group service-card cursor-pointer animate-fade-in-up"
                  onClick={() => navigate(`/booking?service=${featuredServices[0].service_id}`)}
                  data-testid="bento-main"
                >
                  <img
                    src={getFullImageUrl(featuredServices[0].images?.[0]) || 'https://images.pexels.com/photos/5659012/pexels-photo-5659012.jpeg'}
                    alt={featuredServices[0][`name_${language}`]}
                    className="w-full h-full object-cover service-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1815]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 p-8">
                    <span className="text-sm uppercase tracking-widest" style={{ color: primaryColor }}>
                      {featuredServices[0].category || 'Serviço'}
                    </span>
                    <h3 className="font-heading text-3xl text-white mt-2">
                      {featuredServices[0][`name_${language}`]}
                    </h3>
                    <div className="flex items-center gap-4 mt-4 text-white/80">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {featuredServices[0].duration} min
                      </span>
                      <span className="font-semibold" style={{ color: primaryColor }}>€{featuredServices[0].price}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Small Cell 1 - Second Service */}
              {featuredServices[1] && (
                <div
                  className="md:col-span-4 rounded-3xl overflow-hidden relative group service-card cursor-pointer animate-fade-in-up animation-delay-100"
                  onClick={() => navigate(`/booking?service=${featuredServices[1].service_id}`)}
                  data-testid="bento-secondary-1"
                >
                  <img
                    src={getFullImageUrl(featuredServices[1].images?.[0]) || 'https://images.unsplash.com/photo-1722350766824-f8520e9676ac'}
                    alt={featuredServices[1][`name_${language}`]}
                    className="w-full h-full object-cover service-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1815]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <span className="text-xs uppercase tracking-widest" style={{ color: primaryColor }}>
                      {featuredServices[1].category || 'Serviço'}
                    </span>
                    <h3 className="font-heading text-xl text-white mt-1">
                      {featuredServices[1][`name_${language}`]}
                    </h3>
                    <span className="font-semibold" style={{ color: primaryColor }}>€{featuredServices[1].price}</span>
                  </div>
                </div>
              )}

              {/* Small Cell 2 - Third Service */}
              {featuredServices[2] && (
                <div
                  className="md:col-span-4 rounded-3xl overflow-hidden relative group service-card cursor-pointer animate-fade-in-up animation-delay-200"
                  onClick={() => navigate(`/booking?service=${featuredServices[2].service_id}`)}
                  data-testid="bento-secondary-2"
                >
                  <img
                    src={getFullImageUrl(featuredServices[2].images?.[0]) || 'https://images.pexels.com/photos/5659019/pexels-photo-5659019.jpeg'}
                    alt={featuredServices[2][`name_${language}`]}
                    className="w-full h-full object-cover service-image"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A1815]/80 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 p-6">
                    <span className="text-xs uppercase tracking-widest" style={{ color: primaryColor }}>
                      {featuredServices[2].category || 'Serviço'}
                    </span>
                    <h3 className="font-heading text-xl text-white mt-1">
                      {featuredServices[2][`name_${language}`]}
                    </h3>
                    <span className="font-semibold" style={{ color: primaryColor }}>€{featuredServices[2].price}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <p style={{ color: `${textColor}80` }}>
                {language === 'pt' ? 'Serviços em breve...' : 'Services coming soon...'}
              </p>
            </div>
          )}
        </div>
      </section>
                
      {/* CTA Section */}
      <section className="py-24 md:py-32" style={{ backgroundColor: secondaryColor }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <div className="animate-fade-in-up">
            <Sparkles className="w-10 h-10 mx-auto mb-6" style={{ color: primaryColor }} />
            <h2 className="font-heading text-3xl md:text-5xl font-normal text-white">
              <EditableText settingKey={`cta_title_${language}`} defaultValue={language === 'pt' ? 'Pronta para se transformar?' : 'Ready to transform yourself?'} />
            </h2>
            <p className="text-white/70 mt-6 text-lg max-w-xl mx-auto">
              <EditableText 
                settingKey={`cta_subtitle_${language}`} 
                defaultValue={language === 'pt' ? 'Reserve seu momento e conheça o jeitinho da Lou (Lou\'s Way).' : 'Reserve your moment and get to know Lou\'s way.'} 
                multiline 
              />
            </p>
            <Button
              onClick={handleBookClick}
              className="mt-10 rounded-full px-10 py-6 text-sm uppercase tracking-wide transition-colors duration-300"
              style={{ backgroundColor: buttonBgColor, color: buttonTextColor }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = buttonHoverBgColor;
                e.currentTarget.style.color = buttonHoverTextColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = buttonBgColor;
                e.currentTarget.style.color = buttonTextColor;
              }}
              data-testid="cta-book-btn"
            >
              <EditableText settingKey={`btn_book_${language}`} defaultValue={t('home.cta_book')} />
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;